"use client";

import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { Star } from "lucide-react";
import DownloadCTA from "@/components/shared/DownloadCTA";
import StatsBar from "@/components/StatsBar";

const HERO_IMG = "/images/iphone-hero-auswertung.webp";
const HERO_SRCSET =
  "/images/iphone-hero-auswertung-560.webp 560w, /images/iphone-hero-auswertung.webp 840w";
const HERO_SIZES = "(min-width: 1024px) 420px, (min-width: 640px) 360px, 280px";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReduced = useReducedMotion();

  // Phone rotation: settles as section reaches top
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end 0.9"],
  });
  const rotateX = useTransform(scrollYProgress, [0, 1], [32, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [0.9, 1]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [40, 0]);

  // Bottom corner radius: 0 when the bottom edge enters the viewport,
  // 64px once the section has scrolled out the top.
  const { scrollYProgress: edgeProgress } = useScroll({
    target: sectionRef,
    offset: ["end end", "end start"],
  });
  const radius = useTransform(edgeProgress, [0, 1], [0, 64]);

  // Mouse parallax for the floating chips (desktop only – mouse events
  // don't fire on touch). Normalized -1..1 around the container center,
  // spring-smoothed so the chips trail the cursor softly.
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const chipX = useSpring(pointerX, { stiffness: 50, damping: 14, mass: 0.6 });
  const chipY = useSpring(pointerY, { stiffness: 50, damping: 14, mass: 0.6 });

  function handlePointerMove(e: React.MouseEvent<HTMLDivElement>) {
    if (prefersReduced) return;
    const rect = e.currentTarget.getBoundingClientRect();
    pointerX.set(((e.clientX - rect.left) / rect.width - 0.5) * 2);
    pointerY.set(((e.clientY - rect.top) / rect.height - 0.5) * 2);
  }

  function resetPointer() {
    pointerX.set(0);
    pointerY.set(0);
  }

  return (
    <motion.section
      ref={sectionRef}
      id="home"
      className="relative bg-paper overflow-hidden"
      style={
        prefersReduced
          ? undefined
          : { borderBottomLeftRadius: radius, borderBottomRightRadius: radius }
      }
    >
      {/* Soft brand gradient: paper eases into brand blue toward the bottom */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[82%] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(74,67,218,0.03) 34%, rgba(74,67,218,0.12) 54%, rgba(74,67,218,0.45) 70%, rgba(74,67,218,0.85) 84%, #4a43da 94%)",
        }}
      />
      {/* Dezente violette Lichtquelle hinter dem Phone */}
      <div
        aria-hidden="true"
        className="absolute bottom-[26%] left-1/2 -translate-x-1/2 w-[120%] h-[46%] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 46% 52% at 50% 58%, rgba(124,58,237,0.32) 0%, rgba(124,58,237,0.1) 46%, transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 w-full">

        {/* ── Centered copy ──
            CSS entrance (.enter) instead of framer-motion: the headline is the
            LCP element and has to be visible from the first paint, not only
            once the JS bundle has hydrated. */}
        <div className="flex flex-col items-center text-center gap-7">
          <p className="enter text-xs font-bold uppercase tracking-widest text-ink-faint">
            Humanmedizin · Zahnmedizin · Tiermedizin · Pharmazie
          </p>

          <h1 className="enter [--enter-delay:100ms] font-display font-bold tracking-tight text-[2.75rem] leading-[1.1] sm:text-6xl lg:text-7xl text-ink max-w-4xl">
            Berechne deine{" "}
            <span className="text-accent">Chancen</span> aufs Medizinstudium
          </h1>

          <p className="enter [--enter-delay:200ms] text-lg text-ink-soft leading-relaxed max-w-xl">
            Abi, TMS, HamNat: Gib deine Werte ein und sieh in Echtzeit, an
            welchen der 42+ öffentlichen Hochschulen eine Zulassung möglich
            gewesen wäre. Kostenlos, ohne Anmeldung.
          </p>

          <div className="enter [--enter-delay:300ms]">
            <DownloadCTA variant="accent" />
          </div>

          <p className="enter [--enter-delay:400ms] flex items-center gap-2 text-sm text-ink-faint">
            <span className="flex items-center gap-0.5" aria-hidden="true">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={13} className="fill-amber-400 text-amber-400" />
              ))}
            </span>
            <span className="tnum">4,8 Sterne</span>
            <span className="text-ink-faint/40" aria-hidden="true">·</span>
            <span className="tnum">200.000+ Downloads</span>
          </p>
        </div>

        {/* ── Rotating phone + floating chips ── */}
        <div
          className="relative flex justify-center pt-14 lg:pt-20 pb-16 lg:pb-20"
          onMouseMove={handlePointerMove}
          onMouseLeave={resetPointer}
        >
          {/* Entrance via CSS on the wrapper (no hydration wait); the
              scroll-linked rotation stays on the inner motion.div. */}
          <div
            className="enter [--enter-y:56px] [--enter-delay:400ms] [animation-duration:0.9s] relative z-10"
            style={{ perspective: 1200 }}
          >
          <motion.div
            style={
              prefersReduced
                ? undefined
                : { rotateX, scale, y: phoneY, transformStyle: "preserve-3d" }
            }
            className="origin-top will-change-transform"
          >
            {/* Plain <img> with srcSet: images are `unoptimized` (static
                export), so next/image would always ship the 840px file, even
                to phones that render it 280px wide. Deliberately not preloaded
                / high priority: the LCP element is the headline, and on a slow
                connection the image would compete with it for bandwidth. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={HERO_IMG}
              srcSet={HERO_SRCSET}
              sizes={HERO_SIZES}
              alt="ncrechner App: Auswertung der Zulassungschancen an allen Hochschulen"
              width={420}
              height={869}
              decoding="async"
              className="w-[280px] sm:w-[360px] lg:w-[420px] h-auto drop-shadow-2xl"
            />
          </motion.div>
          </div>

          {/* Chip: input (top-left) */}
          <Chip
            className="left-[6%] lg:left-[13%] top-28"
            eyebrow="Deine Werte"
            value="Abi 1,7 · TMS Top 5 %"
            fromX={200}
            fromY={150}
            depth={22}
            pointerX={chipX}
            pointerY={chipY}
            progress={scrollYProgress}
            band={[0, 0.28]}
          />

          {/* Chip: result (top-right) */}
          <Chip
            className="right-[6%] lg:right-[13%] top-52"
            eyebrow="Zulassung möglich"
            value="17 von 43 Unis"
            dot
            fromX={-200}
            fromY={120}
            depth={16}
            pointerX={chipX}
            pointerY={chipY}
            progress={scrollYProgress}
            band={[0.04, 0.32]}
          />

          {/* Chip: community (bottom-left) */}
          <Chip
            className="left-[14%] lg:left-[20%] bottom-28"
            eyebrow="Community Chat"
            value="48.996 Mitglieder"
            dot
            fromX={170}
            fromY={50}
            depth={26}
            pointerX={chipX}
            pointerY={chipY}
            progress={scrollYProgress}
            band={[0.08, 0.36]}
          />

          {/* Chip: all grenzränge (bottom-right) */}
          <Chip
            className="right-[14%] lg:right-[20%] bottom-44"
            eyebrow="Netzwerk"
            value="Community Chat"
            fromX={-170}
            fromY={60}
            depth={12}
            pointerX={chipX}
            pointerY={chipY}
            progress={scrollYProgress}
            band={[0.12, 0.4]}
          />
        </div>
      </div>

      {/* Stats on the same gradient canvas */}
      <div className="relative">
        <StatsBar />
      </div>
    </motion.section>
  );
}

function Chip({
  className,
  eyebrow,
  value,
  dot,
  fromX,
  fromY,
  depth,
  pointerX,
  pointerY,
  progress,
  band = [0, 0.3],
}: {
  className: string;
  eyebrow: string;
  value: string;
  dot?: boolean;
  /** Startversatz Richtung Phone-Mitte – die Chips tauchen hinter dem Phone auf. */
  fromX: number;
  fromY: number;
  /** Parallax-Stärke in px bei voller Mausauslenkung. */
  depth: number;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  /** Scroll-Fortschritt der Hero-Section – die Chips sind daran „angeheftet". */
  progress: MotionValue<number>;
  /** [Start, Ende] im Scroll-Fortschritt, in dem der Chip herausgleitet. */
  band?: [number, number];
}) {
  const prefersReduced = useReducedMotion();
  const parallaxX = useTransform(pointerX, (v) => v * depth);
  const parallaxY = useTransform(pointerY, (v) => v * depth * 0.6);

  // Scroll-gescrubbt statt zeitgesteuert: Position ist deterministisch zur
  // Scroll-Position – auch bei schnellem Scrollen ist der Zustand immer korrekt.
  const [b0, b1] = band;
  const x = useTransform(progress, [b0, b1], [fromX, 0]);
  const y = useTransform(progress, [b0, b1], [fromY, 0]);
  const scale = useTransform(progress, [b0, b1], [0.8, 1]);
  const opacity = useTransform(progress, [b0, b0 + (b1 - b0) * 0.4], [0, 1]);
  // Erst hinter dem Phone (z-0), auf halber Strecke davor (z-20)
  const zIndex = useTransform(progress, (p) =>
    p > b0 + (b1 - b0) * 0.55 ? 20 : 0
  );

  return (
    <motion.div
      style={
        prefersReduced
          ? undefined
          : { x, y, scale, opacity, zIndex }
      }
      className={`hidden md:block absolute z-20 ${className}`}
    >
      <motion.div
        style={prefersReduced ? undefined : { x: parallaxX, y: parallaxY }}
        className="bg-paper border border-line rounded-xl px-4 py-3 shadow-sm"
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-faint">
          {dot && <span className="w-1.5 h-1.5 rounded-full bg-mint" aria-hidden="true" />}
          {eyebrow}
        </span>
        <span className="block mt-1 font-display font-bold text-xl text-ink tnum">
          {value}
        </span>
      </motion.div>
    </motion.div>
  );
}
